"use client";

import { useEffect, useState } from "react";
import { Clock, AlertCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface Props {
  expiresAt: string;
  /** Fires when the timer reaches zero. Caller should open the
   *  "reservation expired" modal (or re-reserve) in response. */
  onExpired: () => void;
}

/**
 * Small sticky banner that shows the customer how long their
 * selected time is held. Drops to amber below 2 minutes, red
 * below 30 seconds. Calls `onExpired` exactly once when the
 * countdown hits zero; caller is responsible for the modal
 * + re-reserve flow.
 */
export default function SlotReservationBanner({ expiresAt, onExpired }: Props) {
  const t = useTranslations("booking");
  const [now, setNow] = useState(() => Date.now());
  const [fired, setFired] = useState(false);
  const expireMs = new Date(expiresAt).getTime();
  const remainingMs = Math.max(0, expireMs - now);

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (remainingMs === 0 && !fired) {
      setFired(true);
      onExpired();
    }
  }, [remainingMs, fired, onExpired]);

  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);
  const mmss = `${String(minutes).padStart(1, "0")}:${String(seconds).padStart(2, "0")}`;

  const lowWarn = remainingMs < 120_000; // < 2 min
  const criticalWarn = remainingMs < 30_000; // < 30 sec

  if (remainingMs === 0) return null; // modal will take over

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2 flex items-center gap-2 text-xs transition-colors",
        criticalWarn
          ? "bg-red-50 border-red-200 text-red-800"
          : lowWarn
          ? "bg-amber-50 border-amber-200 text-amber-900"
          : "bg-emerald-50 border-emerald-200 text-emerald-800"
      )}
      role="status"
      aria-live="polite"
    >
      {criticalWarn ? (
        <AlertCircle className="h-4 w-4 shrink-0" />
      ) : (
        <Clock className="h-4 w-4 shrink-0" />
      )}
      <span className="flex-1">
        {t.rich(
          criticalWarn ? "slotHeldCritical" : lowWarn ? "slotHeldLow" : "slotHeldNormal",
          {
            time: mmss,
            b: (chunks) => <strong>{chunks}</strong>,
          }
        )}
      </span>
    </div>
  );
}
